import type { LayoutDirection } from "./types"
import { getWasmExports } from "./wasmRuntime"

const HEADER_SIZE = 12
const EDGE_STRIDE = 8
const GROUP_STRIDE = 4

interface TessEdge {
  from: number
  to: number
}

interface TessellateInput {
  edges: TessEdge[]
  groupCounts: number[]
  direction: LayoutDirection
}

export interface TessellatedEdges {
  verts: Float32Array
  indices: Uint32Array
  groupIndexCounts: Uint32Array
}

export function tessellateEdges(
  input: TessellateInput,
): TessellatedEdges | null {
  const exports = getWasmExports()
  if (!exports) return null

  const totalSize =
    HEADER_SIZE +
    input.edges.length * EDGE_STRIDE +
    input.groupCounts.length * GROUP_STRIDE
  const inputPtr = exports.alloc_input(totalSize)
  const view = new DataView(exports.memory.buffer, inputPtr, totalSize)

  view.setUint32(0, input.edges.length, true)
  view.setUint32(4, input.groupCounts.length, true)
  view.setUint32(8, input.direction === "RIGHT" ? 1 : 0, true)

  let offset = HEADER_SIZE
  for (const edge of input.edges) {
    view.setUint32(offset, edge.from, true)
    view.setUint32(offset + 4, edge.to, true)
    offset += EDGE_STRIDE
  }
  for (const count of input.groupCounts) {
    view.setUint32(offset, count, true)
    offset += GROUP_STRIDE
  }

  const outPtr = exports.tessellate_edges()
  const header = new DataView(exports.memory.buffer, outPtr, 12)
  const vertFloatCount = header.getUint32(0, true)
  const indexCount = header.getUint32(4, true)
  const groupCount = header.getUint32(8, true)
  const groupOffset = outPtr + 12
  const vertexOffset = groupOffset + groupCount * 4
  const indexOffset = vertexOffset + vertFloatCount * 4

  return {
    verts: new Float32Array(
      exports.memory.buffer,
      vertexOffset,
      vertFloatCount,
    ),
    indices: new Uint32Array(exports.memory.buffer, indexOffset, indexCount),
    groupIndexCounts: new Uint32Array(
      exports.memory.buffer,
      groupOffset,
      groupCount,
    ),
  }
}
